// =========================================================================
//  UNIQUE EXPERIENCES — CONFIG
//  -----------------------------------------------------------------------
//  Every Unique Experience detail page is driven by the object in this
//  file. To add/change content:
//
//  1. Find the experience you want to edit (or copy one to make a new one).
//  2. Swap the image URLs in `heroImage`, `galleryImages`, and optional
//     `accentImage`. URLs can be any public HTTP(S) link — Emergent asset
//     URLs, your own CDN, or Unsplash.
//  3. Update the copy (`title`, `subtitle`, `heroTagline`, `steps`,
//     `bulletPoints`, etc.) to match the activity.
//  4. Register the slug + a route in `/app/frontend/src/App.js` (once).
//
//  The `<ExperienceDetailTemplate />` component renders this config as a
//  full page at `/unique-experiences/:slug`.
// =========================================================================

import { Heart, Utensils, Sparkles, Camera, Palette, Brush, Gift, Cake, Shirt, Wand2, Grape } from 'lucide-react';

// Re-usable "Happy kids" moment photo. Swap to any URL.
const HAPPINESS_ACCENT = 'https://customer-assets.emergentagent.com/job_celebrate-bites/artifacts/e7we2ksu_Happiness_Expression.jpg';

// Each experience config ---------------------------------------------------
// Required fields: slug, icon, brandColor, title, subtitle, heroTagline,
// heroImage { src, alt }, steps (length 4), bulletPoints (array).
// Optional: accentImage { src, alt }, galleryImages (array of {src, alt, caption}).
// -------------------------------------------------------------------------

export const EXPERIENCES_CONFIG = {
  /* ================= DIY FRUIT BOUQUET ================= */
  'diy-fruit-bouquet': {
    slug: 'diy-fruit-bouquet',
    icon: Grape,
    brandColor: '#FF3B6B',                // pink — used for pills, buttons, highlights
    accentBg: '#FFE8EC',                  // soft icon-card background
    heroGradient: 'linear-gradient(135deg, #FFE8EC 0%, #FFF4E6 30%, #EDFFF8 70%, #F0F0FF 100%)',
    ctaGradient: 'linear-gradient(135deg, #FF3B6B 0%, #FF7A9C 100%)',
    title: 'DIY Fruit Bouquet',
    subtitle: 'Unique Experience',
    heroTagline:
      "The one station kids keep talking about after the party. Every guest builds their own colourful, skewered fruit bouquet — part craft, part snack, entirely unforgettable.",
    heroImage: {
      src: 'https://customer-assets.emergentagent.com/job_celebrate-bites/artifacts/slhxhj0v_Happy_Birthday_DFD_Confetti_Berries.jpg',
      alt: 'Happy Birthday fruit bouquet with strawberries, melon flowers and confetti treats',
    },
    accentImage: { src: HAPPINESS_ACCENT, alt: 'Happy kids at a Creative Birthdays station' },
    realMomentCaption: 'Average build time: just 15-20 minutes per child.',
    steps: [
      { icon: Heart,    title: 'We prep everything', desc: 'Fresh strawberries, pineapple, grapes, melon — pre-washed, pre-cut and laid out at kid-friendly stations.' },
      { icon: Utensils, title: 'Pick, poke, build',  desc: 'Each child picks their favourite fruits and skewers them onto food-safe picks with a party host guiding the fun.' },
      { icon: Sparkles, title: 'Arrange into a bouquet', desc: 'Kids plant their skewers into a playful mini-pot or vase to create their very own edible bouquet.' },
      { icon: Camera,   title: 'Photo + eat',        desc: "A quick group photo with everyone's creations — then the best part: eating their beautiful, handmade bouquet!" },
    ],
    experienceHeading: 'Creative, hands-on, and deliciously proud',
    experienceCopy: [
      "There's something magical about handing a child a pile of colourful fruit and watching them turn it into a bouquet they can actually eat. No screens, no passive watching — just creative, tactile play that pays off in giggles, group photos, and a burst of natural sugar energy.",
      "Each guest goes home with a reusable mini-vase, the memory of their finished creation, and (if any survived the table) a couple of skewers wrapped for the car ride. Parents love that it's healthy. Kids love that it's theirs.",
    ],
    bulletPoints: [
      'A totally hands-on, screen-free activity that engages every child',
      'Naturally inclusive — works for every allergy, diet, and dexterity level',
      'Edible + Instagrammable: the kids eat their own colourful masterpiece',
      'Zero cleanup for you — we arrive, set up, run it, and leave the space spotless',
      'Perfect for ages 4-12; hosts adapt guidance per age group',
    ],
    galleryImages: [
      { src: 'https://customer-assets.emergentagent.com/job_celebrate-bites/artifacts/76z6lc23_Birthday_BFruit_Flight_Delight.jpg',
        alt: 'Birthday fruit flight — pineapple stars, strawberries and chocolate treats',
        caption: 'Birthday fruit flight — kids pick their favourite shapes to assemble.' },
      { src: 'https://customer-assets.emergentagent.com/job_celebrate-bites/artifacts/x5rar0k5_Just_Because_Bouquet_Dipped_Strawberries_lg_s.webp',
        alt: 'Chocolate-dipped strawberry bouquet with a Happy Birthday balloon',
        caption: 'Chocolate-dipped finishers — the kids\' favourite "final touch" station.' },
      { src: 'https://customer-assets.emergentagent.com/job_celebrate-bites/artifacts/35bryh8e_Mini_Berry_Arrangement.jpg',
        alt: 'Mini chocolate-drizzled strawberry arrangement in a blue glass vase',
        caption: 'Mini berry arrangements — each child leaves with one to take home.' },
    ],
    ctaHeading: 'Ready to build edible art at your party?',
    ctaCopy: 'Add the DIY Fruit Bouquet station to any Creative Birthdays package — or build a custom party around it.',
  },

  /* ================= ART & CRAFT CORNER ================= */
  'art-and-craft-corner': {
    slug: 'art-and-craft-corner',
    icon: Palette,
    brandColor: '#6366F1',
    accentBg: '#F0F0FF',
    heroGradient: 'linear-gradient(135deg, #F0F0FF 0%, #FFF4E6 50%, #FFE8EC 100%)',
    ctaGradient: 'linear-gradient(135deg, #6366F1 0%, #A78BFA 100%)',
    title: 'Art & Craft Corner',
    subtitle: 'Unique Experience',
    heroTagline:
      'Paint, glue, glitter, stickers — the craft corner kids disappear into the moment they arrive. Every masterpiece comes home on the fridge.',
    heroImage: {
      src: 'https://images.unsplash.com/photo-1607453998774-d533f65dac99?auto=format&fit=crop&w=900&q=80',
      alt: 'Rainbow of colourful paint, brushes and craft supplies laid out on a kids craft table',
    },
    accentImage: { src: HAPPINESS_ACCENT, alt: 'Happy kids at a Creative Birthdays station' },
    realMomentCaption: 'Every kid leaves with 2-3 finished pieces.',
    steps: [
      { icon: Brush,    title: 'Open the art caddies',  desc: 'Paints, glue, glitter, googly eyes, stickers and foam shapes laid out in individual trays so nothing gets chaotic.' },
      { icon: Palette,  title: 'Theme of the day',      desc: 'Choose a theme at booking (unicorns, space, jungle) — we bring matching templates and decals.' },
      { icon: Sparkles, title: 'Host-led demo',         desc: 'A short group demo sparks inspiration; then kids personalise freely at their own pace.' },
      { icon: Gift,     title: 'Dry, wrap, take home',  desc: 'We drying-rack the pieces, wrap them in a gift bag, and hand them to the parents at pickup.' },
    ],
    experienceHeading: 'Creativity that lasts well beyond the party',
    experienceCopy: [
      'Unlike a bouncy-house-style activity that ends when the music stops, craft corner creations go home and live on the fridge, bedroom wall, or backpack zipper for months.',
      'Parents tell us the art corner is the single most-asked-for station at second-time bookings — because kids proudly show off what they made long after the cake is gone.',
    ],
    bulletPoints: [
      'Age-appropriate templates for 4-6, 7-9, and 10-12 year olds',
      'Non-toxic, washable supplies only — we think about the laundry',
      'Themed kits: unicorn, dino, astronaut, jungle, superhero, custom',
      'Every piece is wrapped and labelled with the child\'s name',
      'Tidy is built-in — we pack out every last glitter speck',
    ],
    galleryImages: [
      { src: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=1200&q=80',
        alt: 'Colourful crayons fanned out in a rainbow pattern',
        caption: 'Non-toxic crayons, paints, and markers for every age group.' },
      { src: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1200&q=80',
        alt: 'Little hands painting on a craft table',
        caption: 'Hands-on creativity — no screens, just colour and imagination.' },
      { src: 'https://images.unsplash.com/photo-1528459801416-a9e53bbf4e17?auto=format&fit=crop&w=1200&q=80',
        alt: 'Assorted art supplies — brushes, paper, scissors — neatly arranged',
        caption: 'Every station is prepped and tidy before kids arrive.' },
    ],
    ctaHeading: 'Bring the craft corner to your next party',
    ctaCopy: 'Add this station to any package — it pairs beautifully with the DIY Fruit Bouquet.',
  },

  /* ================= BRACELET & KEYCHAIN BAR ================= */
  'bracelet-and-keychain-bar': {
    slug: 'bracelet-and-keychain-bar',
    icon: Sparkles,
    brandColor: '#FFD166',
    accentBg: '#FFF8E7',
    heroGradient: 'linear-gradient(135deg, #FFF8E7 0%, #FFE8EC 50%, #F0F0FF 100%)',
    ctaGradient: 'linear-gradient(135deg, #F59E0B 0%, #FFD166 100%)',
    title: 'Bracelet & Keychain Bar',
    subtitle: 'Unique Experience',
    heroTagline:
      'Beads, charms and letters laid out like a tiny candy store. Every kid designs a bracelet or keychain they\'ll wear until it falls off.',
    heroImage: {
      src: 'https://placehold.co/900x1125/FFD166/1E1E24/png?text=Bracelets+%26+Beads%0ASwap+Me&font=raleway',
      alt: 'Placeholder — replace with a real bracelet/bead photo',
    },
    accentImage: { src: HAPPINESS_ACCENT, alt: 'Happy kids at a Creative Birthdays station' },
    realMomentCaption: 'Typical child makes 1 bracelet + 1 keychain in 20 min.',
    steps: [
      { icon: Sparkles, title: 'Pick your palette',  desc: 'Choose from 6 theme palettes — pastel, neon, rainbow, earth, sports, or monster.' },
      { icon: Wand2,    title: 'Letters & charms',   desc: 'Spell your name, your BFF\'s, or a secret word — charms include stars, animals, hearts, and sports balls.' },
      { icon: Heart,    title: 'Host helps secure',  desc: 'Our hosts tie the elastic or clasp so nothing snaps on the way home.' },
      { icon: Gift,     title: 'Wear it out',        desc: 'Kids leave wearing the bracelet — the keychain clips to their backpack.' },
    ],
    experienceHeading: 'Wearable memories every guest takes home',
    experienceCopy: [
      'Bracelets are the thank-you gift that never feels like a plastic party favour. Kids made them, so kids wear them — for weeks.',
      'We lay out the beads like a bead-bar (Bead Bar!) so there\'s no running out, no fighting, no "she took my charm" meltdowns.',
    ],
    bulletPoints: [
      '6 themed palettes keep everything cohesive',
      'Charms, letter beads, and animal shapes — 80+ options',
      'Elastic + clasp bracelets both available depending on age',
      'Matching keychain station for kids who want a "for the backpack" memento',
      'Clean setup / pack-up — no stray beads in the carpet',
    ],
    galleryImages: [
      { src: 'https://placehold.co/900x1125/F59E0B/FFFFFF/png?text=Bracelets%0AGallery+1&font=raleway',
        alt: 'Placeholder — swap with a beads/bracelet photo',
        caption: 'Finished bracelets — each one uniquely the kid who made it.' },
      { src: 'https://placehold.co/900x1125/FFD166/1E1E24/png?text=Charms+%26+Letters%0AGallery+2&font=raleway',
        alt: 'Placeholder — swap with a charm/letter-bead photo',
        caption: '80+ charm and letter options — spell anything you want.' },
      { src: 'https://placehold.co/900x1125/FBBF24/1E1E24/png?text=Bead+Bar%0AGallery+3&font=raleway',
        alt: 'Placeholder — swap with a bead station photo',
        caption: 'Hosts help with clasps so nothing snaps on the way home.' },
    ],
    ctaHeading: 'Add the bead bar to your next party',
    ctaCopy: 'Perfect as a "chill station" next to a more active activity like the DIY Fruit Bouquet.',
  },

  /* ================= CUSTOM CAKE STUDIO ================= */
  'custom-cake-studio': {
    slug: 'custom-cake-studio',
    icon: Cake,
    brandColor: '#06D6A0',
    accentBg: '#EDFFF8',
    heroGradient: 'linear-gradient(135deg, #EDFFF8 0%, #FFF8E7 50%, #FFE8EC 100%)',
    ctaGradient: 'linear-gradient(135deg, #059669 0%, #06D6A0 100%)',
    title: 'Custom Cake Studio',
    subtitle: 'Unique Experience',
    heroTagline:
      'Choose the flavour, filling, and theme. We bake it fresh the day of the party, and (optional) let the kids decorate the final layer themselves.',
    heroImage: {
      src: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?auto=format&fit=crop&w=900&q=80',
      alt: 'Decorated three-tier birthday cake with sprinkles and bright toppings',
    },
    accentImage: { src: HAPPINESS_ACCENT, alt: 'Happy kids at a Creative Birthdays station' },
    realMomentCaption: 'We bake fresh the morning of the party.',
    steps: [
      { icon: Cake,     title: 'Pick a theme',        desc: 'Unicorn, dino, superhero, princess, sports team, or "go wild" — we\'ll send a sketch for approval.' },
      { icon: Utensils, title: 'Choose flavour + filling', desc: 'Vanilla, chocolate, funfetti, strawberry, red velvet — with allergy-safe alternatives available.' },
      { icon: Wand2,    title: 'Decorate station (optional)', desc: 'Add the kid-decorating add-on and each guest finishes a mini-cupcake with piping bags and sprinkles.' },
      { icon: Sparkles, title: 'Candle + serve',      desc: 'We handle the reveal, the candles, and the "Happy Birthday" moment photo.' },
    ],
    experienceHeading: 'The cake is always the star — make it unforgettable',
    experienceCopy: [
      'Every kid remembers the cake. Make yours the one they talk about at school the next Monday.',
      'Our baker can match almost any theme — if you can describe it, we can usually bake it. Dietary-restriction versions (gluten-free, dairy-free, nut-free) are available on 5+ days notice.',
    ],
    bulletPoints: [
      'Freshly baked on party day, never a day-old cake',
      'Custom fondant shapes + edible prints available',
      'Optional "kids decorate a cupcake" add-on station',
      'Allergy-safe versions available with 5-day lead time',
      'Every cake comes with coordinated candles + a slice knife',
    ],
    galleryImages: [
      { src: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&w=1200&q=80',
        alt: 'Stack of bright, decorated cupcakes with colourful frosting',
        caption: 'Optional kid-decorated cupcake station pairs perfectly.' },
      { src: 'https://images.unsplash.com/photo-1606313564200-e75d5e30476c?auto=format&fit=crop&w=1200&q=80',
        alt: 'Pastel pink drip cake with sprinkles and a candle',
        caption: 'Fully custom designs — send inspiration, we match it.' },
      { src: 'https://images.unsplash.com/photo-1535141192574-5d4897c12636?auto=format&fit=crop&w=1200&q=80',
        alt: 'Close-up of rainbow sprinkle birthday cupcake',
        caption: 'Allergy-safe versions available with 5 days notice.' },
    ],
    ctaHeading: 'Design your dream cake today',
    ctaCopy: "Book a party or send us your inspiration photo — we'll come back with a sketch within one business day.",
  },

  /* ================= PRINT YOUR OWN GIFT ================= */
  'print-your-own-gift': {
    slug: 'print-your-own-gift',
    icon: Shirt,
    brandColor: '#FF9F45',
    accentBg: '#FFF0E6',
    heroGradient: 'linear-gradient(135deg, #FFF0E6 0%, #FFE8EC 50%, #F0F0FF 100%)',
    ctaGradient: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
    title: 'Print Your Own Gift',
    subtitle: 'Unique Experience',
    heroTagline:
      'Kids design their own cap, tee, or mug — then watch it print live on our heat press. A souvenir they *actually* made themselves.',
    heroImage: {
      src: 'https://images.unsplash.com/photo-1562157873-818bc0726f68?auto=format&fit=crop&w=900&q=80',
      alt: 'Row of colourful custom t-shirts hanging on wooden hangers',
    },
    accentImage: { src: HAPPINESS_ACCENT, alt: 'Happy kids at a Creative Birthdays station' },
    realMomentCaption: 'Every child takes home their finished print.',
    steps: [
      { icon: Palette,  title: 'Design on the tablet',  desc: 'Drag text, stickers, and drawings onto a blank garment template in our kid-friendly iPad designer.' },
      { icon: Sparkles, title: 'Preview + tweak',       desc: 'See a 3D preview instantly; tweak until they love it. Names, ages, and inside jokes all welcome.' },
      { icon: Shirt,    title: 'Live heat-press',       desc: 'Our host presses the design in front of the group — the "ohhh!" moment is why we built this.' },
      { icon: Gift,     title: 'Wear it at the party',  desc: 'Designs come off the press in 90 seconds and are usually worn at the party\'s next activity.' },
    ],
    experienceHeading: 'The souvenir they made themselves',
    experienceCopy: [
      'Most party favours are forgettable. This one isn\'t — because the kid designed it from scratch.',
      'Caps, tees, mugs, and tote bags available. Group discounts when you add this as your main station.',
    ],
    bulletPoints: [
      'Caps, T-shirts, mugs, and tote bags all supported',
      'Kid-friendly iPad design app with stickers + text',
      'Live heat-press means zero "will it be ready?" anxiety',
      'Sizes XS (4-5) through adult L available',
      'Optional: birthday child gets a premium "headliner" piece',
    ],
    galleryImages: [
      { src: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?auto=format&fit=crop&w=1200&q=80',
        alt: 'Neatly folded stack of plain t-shirts ready for printing',
        caption: 'Blank canvas ready for every kid\'s imagination.' },
      { src: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=1200&q=80',
        alt: 'Finished custom-printed t-shirt laid flat with graphic design',
        caption: 'Heat-pressed in 90 seconds — worn before the cake comes out.' },
      { src: 'https://images.unsplash.com/photo-1581655353564-df123a1eb820?auto=format&fit=crop&w=1200&q=80',
        alt: 'Colourful custom printed t-shirts hanging on a rack',
        caption: 'Caps, tees, mugs, and totes — everyone goes home with a souvenir.' },
    ],
    ctaHeading: 'Let every guest design their own souvenir',
    ctaCopy: 'Add the print station to any package — caps, tees, mugs and totes are all on the menu.',
  },

  /* ================= CUSTOM PACKAGE BUILDER ================= */
  'custom-package-builder': {
    slug: 'custom-package-builder',
    icon: Gift,
    brandColor: '#FF3B6B',
    accentBg: '#FFEFF5',
    heroGradient: 'linear-gradient(135deg, #FFEFF5 0%, #FFF8E7 50%, #EDFFF8 100%)',
    ctaGradient: 'linear-gradient(135deg, #FF3B6B 0%, #FF7A9C 100%)',
    title: 'Custom Package Builder',
    subtitle: 'Unique Experience',
    heroTagline:
      "Mix activities, food, and decor into one seamless party. You pick; we plan, source, and run every single detail.",
    heroImage: {
      src: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=900&q=80',
      alt: 'Colourful kids party setup with balloons, pink backdrop and decor',
    },
    accentImage: { src: HAPPINESS_ACCENT, alt: 'Happy kids at a Creative Birthdays station' },
    realMomentCaption: 'Average custom party has 3 stations + cake reveal.',
    steps: [
      { icon: Wand2,    title: 'Discovery call',    desc: "A 15-min chat (phone or Zoom) where we learn the birthday kid's interests, your budget, and must-haves." },
      { icon: Sparkles, title: 'We send a plan',    desc: 'Within 24 hours: a detailed plan with activities, food, decor, and timeline — priced to your budget.' },
      { icon: Cake,     title: 'You tweak, approve', desc: 'Swap or remove anything until it feels perfect. Lock it in 2+ weeks before the big day.' },
      { icon: Gift,     title: 'We run the party',  desc: 'Host team arrives, sets up, runs every station, serves food, and leaves the space spotless.' },
    ],
    experienceHeading: 'One call, one party, zero stress',
    experienceCopy: [
      "Custom parties are where we shine — because no two kids are alike. Tell us what your child loves, what they're scared of, what their friends can/can't eat, and we'll build a party around them.",
      "Ideal for milestone birthdays (5, 8, 10, 13), twins/siblings birthdays, or any time the standard packages don't quite fit.",
    ],
    bulletPoints: [
      'Fully bespoke — any combination of our activities + originals',
      'We handle catering, decor, host staff, and teardown',
      'Clear up-front pricing, no day-of surprises',
      'Perfect for milestone birthdays and blended guest lists',
      'Typical custom parties cost less than you\'d think — ask for a quote',
    ],
    galleryImages: [
      { src: 'https://images.unsplash.com/photo-1464349153735-7db50ed83c84?auto=format&fit=crop&w=1200&q=80',
        alt: 'Colourful balloons and birthday decor for a kids party',
        caption: 'Themed decor delivered and installed by our team.' },
      { src: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?auto=format&fit=crop&w=1200&q=80',
        alt: 'Party table with pink and gold balloons, cupcakes and cake',
        caption: 'End-to-end setup — you just show up and enjoy the day.' },
      { src: 'https://images.unsplash.com/photo-1558636508-e0db3814bd1d?auto=format&fit=crop&w=1200&q=80',
        alt: 'Colourful balloon bouquet for a birthday celebration',
        caption: 'The moment it all comes together — we capture it too.' },
    ],
    ctaHeading: 'Let\'s design your perfect party',
    ctaCopy: 'Tell us about your birthday kid and we\'ll come back with a tailored plan within one business day.',
  },
};
